import {
    type FC,
    useState,
    useEffect,
    useCallback,
    type ComponentProps,
    useTransition,
} from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import classnames from 'classnames';
import serverVars from 'server-vars';
import { trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { PageLinks } from 'dibs-page-links/exports/PageLinks.tsx';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { useDeviceType } from '../../nftHeader/hooks/useDeviceType.tsx';
import { usePageType } from '../../nftHeader/hooks/usePageType.tsx';
import { MARKET, SEARCH } from '../../nftHeader/pageType.ts';
import { NftMarketSortAndFilter } from './NftMarketSortAndFilter/NftMarketSortAndFilter.tsx';
import { NftMarketNoResults } from './NftMarketSortAndFilter/NftMarketNoResults.tsx';
import { type FilterShape } from './NftMarketSortAndFilter/filterConfig.ts';
import { getFilterConfig } from './NftMarketSortAndFilter/filterConfig.ts';
import {
    urlParamsToFilterState,
    filterStateToSearchQueryInput,
} from './NftMarketSortAndFilter/filterTransformations.ts';
import { getSecondaryMarketCount } from '../../utils/getSearchFacetCount.ts';

import styles from './styles/NftMarketGrid.scss';

import { type NftMarketGrid_viewer$key } from './__generated__/NftMarketGrid_viewer.graphql.ts';
import { type NftMarketGridRefetchQuery } from './__generated__/NftMarketGridRefetchQuery.graphql.ts';

// get original query variables for refetch
const variables = serverVars.get('relayVariables');

type TileProps = Extract<ComponentProps<typeof NftItemTile>, { tileType: 'REGULAR_TILE' }>;
type TileAuction = TileProps['nftAuction'];
type TileItem = TileProps['nftItem'];

type ItemsAndAuctions = {
    id: string | null;
    nftItem: TileItem;
    nftAuction: TileAuction | null;
    name?: string | null;
    brand?: string | null;
}[];
type Props = {
    viewer: NftMarketGrid_viewer$key | null | undefined;
    startingPage: number;
    startingFilters: FilterShape;
};

const TilePlaceholder: FC = () => (
    <li className={styles.gridItem}>
        <div className={styles.placeholderImage} />
        <div className={styles.placeholderDetails}>
            <div className={styles.placeholderCreator} />
            <div className={styles.placeholderTitle} />
        </div>
    </li>
);

const placeholderTiles = [...Array(variables?.itemCount || 0)].map((_, i) => (
    <TilePlaceholder key={`placeholder-${i}`} />
));

type TrackingData = {
    list: string;
    id: string | null;
    name?: string | null;
    brand?: string | null;
    index: number;
};

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

const track = (
    type: typeof ECOM_PRODUCT_CLICK | typeof ECOM_PRODUCT_IMPRESSION,
    { index, ...rest }: TrackingData
): void => {
    let eventName;
    switch (type) {
        case ECOM_PRODUCT_CLICK:
            eventName = EVENT_SELECT_ITEM;
            break;
        case ECOM_PRODUCT_IMPRESSION:
            eventName = EVENT_VIEW_ITEM_LIST;
            break;
    }
    trackEcommerce({
        type,
        eventName,
        category: '/nft/art',
        actionField: { itemId: rest.id, list: rest.list },
        products: [{ category: '/nft/art', variant: 'NFT', position: index + 1, ...rest }],
    });
};

export const NftMarketGrid: FC<Props> = ({ viewer: viewerRef, startingPage, startingFilters }) => {
    const { isMobile } = useDeviceType();
    const pageType = usePageType();
    const { currency } = useCurrencyStore();
    const [isPending, startTransition] = useTransition();
    const [currentMarketPage, setCurrentMarketPage] = useState(startingPage);
    const [filterState, setFilterState] = useState<FilterShape>(startingFilters);
    const isMarket = pageType === MARKET;
    const isSearch = pageType === SEARCH;

    const [viewer, refetch] = useRefetchableFragment<
        NftMarketGridRefetchQuery,
        NftMarketGrid_viewer$key
    >(
        graphql`
            fragment NftMarketGrid_viewer on Viewer
            @argumentDefinitions(
                after: { type: "String" }
                auctionStatuses: {
                    type: "[NftItemSearchAuctionStatus!]"
                    defaultValue: [
                        ITEM_SEARCH_AUCTION_LIVE
                        ITEM_SEARCH_AUCTION_SOLD
                        ITEM_SEARCH_AUCTION_UPCOMING
                    ]
                }
                auctionMarketTypes: { type: "[NftItemSearchAuctionMarketType!]" }
                mediumTypes: { type: "[NftItemSearchMediumType!]" }
                sortBy: { type: "NftItemSearchSort" }
                search: { type: "String" }
                page: { type: "Int" }
            )
            @refetchable(queryName: "NftMarketGridRefetchQuery") {
                ...NftHeartPillProvider_viewer
                ...NftItemTile_viewer
                nftItemSearch(
                    first: $itemCount
                    after: $after
                    page: $page
                    auctionStatuses: $auctionStatuses
                    auctionMarketTypes: $auctionMarketTypes
                    mediumTypes: $mediumTypes
                    sortBy: $sortBy
                    search: $search
                ) @connection(key: "NftMarketGrid_nftItemSearch") {
                    totalResults
                    pageInfo {
                        hasNextPage
                    }
                    edges {
                        node {
                            serviceId
                            creatorAddress {
                                publicProfile {
                                    profileName
                                }
                            }
                            metadata {
                                ... on NftDigitalArtMetadata {
                                    name
                                }
                            }
                            mostRecentAuction {
                                ...NftItemTile_nftAuction
                            }
                            ...NftItemTile_nftItem
                        }
                    }
                }
                nftItemSearchFacets: nftItemSearch(first: 0) {
                    ...NftMarketSortAndFilter_search
                    facets {
                        facetField
                        facetValues {
                            value
                            count
                        }
                    }
                }
            }
        `,
        viewerRef
    );

    const list = `${pageType} module`;

    const { nftItemSearch } = viewer || {};

    const nftItemCount = isMobile ? 16 : 40;
    const itemSearchResultCount = nftItemSearch?.totalResults ?? 0;
    const pageTotal = Math.ceil(itemSearchResultCount / nftItemCount);

    let itemsAndAuctions: ItemsAndAuctions | null = null;
    const itemIds: string[] = [];

    itemsAndAuctions = [];
    for (const edge of nftItemSearch?.edges || []) {
        if (edge?.node && edge.node.serviceId && edge.node.mostRecentAuction) {
            itemIds.push(edge.node.serviceId);
            itemsAndAuctions.push({
                id: edge.node.serviceId,
                nftItem: edge.node,
                nftAuction: edge.node.mostRecentAuction,
                name: edge.node.metadata?.name,
                brand: edge.node.creatorAddress?.publicProfile?.profileName,
            });
        }
    }

    const itemsAndActionsTiles =
        viewer &&
        itemsAndAuctions.map(({ nftItem, nftAuction, ...rest }, index) => (
            <li className={styles.gridItem} key={rest.id || index}>
                <NftItemTile
                    tileType="REGULAR_TILE"
                    gaLabel="NFT-Marketplace"
                    isMobile={isMobile}
                    nftAuction={nftAuction}
                    nftItem={nftItem}
                    viewer={viewer}
                    currency={currency}
                    fireImpressionTracking={() =>
                        track(ECOM_PRODUCT_IMPRESSION, { list, index, ...rest })
                    }
                    fireTracking={() => track(ECOM_PRODUCT_CLICK, { list, index, ...rest })}
                />
            </li>
        ));

    const handleHeaderLinks = useCallback(
        (page: number): void => {
            const localeUrl = window.location.href;
            const blankUrl = localeUrl.split('?')[0];

            const prev = document.querySelector('link[rel="prev"]');
            if (prev) {
                if (page > 2) {
                    prev.setAttribute(
                        'href',
                        handleLocaleUrl(`${blankUrl}?page=${page - 1}`, GLOBAL_CLIENT_ONLY_LOCALE)
                    );
                } else if (page === 2) {
                    prev.setAttribute('href', handleLocaleUrl(blankUrl, GLOBAL_CLIENT_ONLY_LOCALE));
                } else if (page === 1) {
                    prev.remove();
                }
            } else {
                const link = document.createElement('link');
                if (page === 2) {
                    link.href = handleLocaleUrl(blankUrl, GLOBAL_CLIENT_ONLY_LOCALE);
                } else {
                    link.href = handleLocaleUrl(
                        `${blankUrl}?page=${page - 1}`,
                        GLOBAL_CLIENT_ONLY_LOCALE
                    );
                }
                link.rel = 'prev';
                document.getElementsByTagName('head')[0].appendChild(link);
            }

            const next = document.querySelector('link[rel="next"]');
            const hasNext = page + 1 <= pageTotal;
            if (next) {
                if (hasNext) {
                    next.setAttribute(
                        'href',
                        handleLocaleUrl(`${blankUrl}?page=${page + 1}`, GLOBAL_CLIENT_ONLY_LOCALE)
                    );
                } else {
                    next.remove();
                }
            } else if (hasNext) {
                const link = document.createElement('link');
                link.href = handleLocaleUrl(
                    `${blankUrl}?page=${page + 1}`,
                    GLOBAL_CLIENT_ONLY_LOCALE
                );
                link.rel = 'next';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
        },
        [pageTotal]
    );

    const handleSearchParamChange = useCallback(
        (page: number, filterUrl?: URL): void => {
            // default to current window search params ONLY IF filterUrl is UNDEFINED
            const urlParams = new URLSearchParams(filterUrl?.search ?? window.location.search);
            if (page === 1) {
                urlParams.delete('page');
            } else if (page) {
                urlParams.set('page', page.toString());
            }
            // default to current window path without search params if updated search params are empty
            const updatedUrl = urlParams.toString() ? `?${urlParams}` : location.pathname;
            window.history.pushState(
                {},
                'NFT Market Pagination',
                handleLocaleUrl(updatedUrl, GLOBAL_CLIENT_ONLY_LOCALE)
            );
            if (isMarket) {
                handleHeaderLinks(page);
            }
            window.scrollTo({ top: 0, left: 0 });
        },
        [isMarket, handleHeaderLinks]
    );

    const showSecondaryMarket = !!getSecondaryMarketCount(viewer?.nftItemSearchFacets?.facets);
    const setPageFromParams = useCallback(() => {
        const { search } = location;
        const params = new URLSearchParams(search);

        const filterParams = urlParamsToFilterState(
            getFilterConfig({
                upcoming: false,
                secondaryMarket: showSecondaryMarket,
            }),
            params
        );
        setFilterState(filterParams);

        const page = !isNaN(Number(params.get('page'))) ? parseInt(params.get('page') || '1') : 1;

        setCurrentMarketPage(page);

        startTransition(() => {
            refetch({ page: page, ...filterStateToSearchQueryInput(filterParams) });
        });

        if (isMarket) {
            handleHeaderLinks(page);
        }
    }, [
        setCurrentMarketPage,
        refetch,
        startTransition,
        handleHeaderLinks,
        isMarket,
        showSecondaryMarket,
    ]);

    const onFilterChange = useCallback(
        (filters: FilterShape, filterUrl: URL): void => {
            setCurrentMarketPage(1);
            handleSearchParamChange(1, filterUrl);
            setFilterState(filters);
            startTransition(() => {
                refetch({ page: 1, ...filterStateToSearchQueryInput(filters) });
            });
        },
        [refetch, startTransition, handleSearchParamChange]
    );

    const onPageChange = (obj: { pageNumber: number }): void => {
        const page = obj.pageNumber;
        handleSearchParamChange(page);
        setCurrentMarketPage(page);
        startTransition(() => {
            refetch({ page, ...filterStateToSearchQueryInput(filterState) });
        });
    };

    useEffect(() => {
        window.onpopstate = () => {
            setPageFromParams();
        };
        return () => {
            window.onpopstate = null;
        };
    }, [setPageFromParams]);

    return (
        <NftHeartPillProvider viewer={viewer} nftItemIds={itemIds}>
            <div className={styles.container}>
                <NftMarketSortAndFilter
                    onFilterChange={onFilterChange}
                    search={viewer?.nftItemSearchFacets}
                    totalResults={itemSearchResultCount}
                    isHidden={!isMarket && !isSearch}
                    startingFilters={filterState}
                />
                <ul
                    className={classnames(styles.grid, {
                        [styles.isSearch]: isSearch,
                        [styles.emptySearch]: itemSearchResultCount === 0,
                    })}
                >
                    {isPending ? placeholderTiles : itemsAndActionsTiles}
                </ul>
                {itemSearchResultCount === 0 ? <NftMarketNoResults /> : null}
                {pageTotal > 1 && (
                    <div className={classnames(styles.viewMoreContainer, styles.marketPagination)}>
                        <PageLinks
                            numberOfPages={pageTotal}
                            currentPage={currentMarketPage}
                            onPageChange={onPageChange}
                        />
                    </div>
                )}
            </div>
        </NftHeartPillProvider>
    );
};
